import { useEffect, useRef, useState } from 'react'

/**
 * Cloudflare Turnstile on the sign-up form.
 *
 * The script is loaded on demand and the widget rendered into a div the
 * caller owns. What comes back is the token to send with the sign-up, or null
 * while the check is still running. With no site key there is no widget and
 * the token is an empty string -- the server then skips the check too.
 */

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string
      reset: (id: string) => void
      remove: (id: string) => void
    }
  }
}

const SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

let loading: Promise<void> | null = null
function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve()
  if (!loading) {
    loading = new Promise((resolve, reject) => {
      const s = document.createElement('script')
      s.src = SCRIPT
      s.async = true
      s.onload = () => resolve()
      s.onerror = () => {
        loading = null
        reject(new Error('Turnstile did not load'))
      }
      document.head.appendChild(s)
    })
  }
  return loading
}

export function useTurnstile(siteKey: string | null) {
  const ref = useRef<HTMLDivElement>(null)
  const [token, setToken] = useState<string | null>(siteKey === '' ? '' : null)
  const [failed, setFailed] = useState(false)
  const widget = useRef<string | null>(null)

  useEffect(() => {
    if (!siteKey) {
      setToken(siteKey === '' ? '' : null)
      return
    }
    let cancelled = false
    loadScript()
      .then(() => {
        if (cancelled || !ref.current || !window.turnstile) return
        widget.current = window.turnstile.render(ref.current, {
          sitekey: siteKey,
          callback: (t: string) => setToken(t),
          'expired-callback': () => setToken(null),
          'error-callback': () => setFailed(true),
          theme: 'auto',
        })
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
      if (widget.current && window.turnstile) {
        try {
          window.turnstile.remove(widget.current)
        } catch {
          /* already gone */
        }
      }
      widget.current = null
    }
  }, [siteKey])

  /** After a failed submit: Turnstile tokens are single-use. */
  const reset = () => {
    if (widget.current && window.turnstile) window.turnstile.reset(widget.current)
    setToken(siteKey ? null : '')
  }

  return { ref, token, failed, reset }
}
